/**
 * SOPIntelligencePanel — executor performance leaderboard (speed score vs SOP
 * average), slowest steps by measured duration, and weekly run trend.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtDuration } from "@/lib/format";
import { Trophy, Turtle, TrendingUp, Zap } from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";

export default function SOPIntelligencePanel({ wsId }) {
  const [days, setDays] = useState("90");
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (!wsId) return;
    setBusy(true);
    api.get(`/workspaces/${wsId}/sop-intelligence?days=${days}`)
      .then((r) => setD(r.data))
      .catch((e) => toast.error(apiErrorMessage(e, "Failed to load intelligence")))
      .finally(() => setBusy(false));
  }, [wsId, days]);

  if (busy && !d) return <div className="text-sm">Loading intelligence…</div>;
  if (!d) return null;

  return (
    <div className="space-y-5" data-testid="sop-intelligence">
      <div className="flex items-center gap-3">
        <div className="label-eyebrow flex items-center gap-1.5"><Zap className="w-3.5 h-3.5" /> SOP Intelligence</div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="h-8 w-32 rounded-none text-xs ml-auto" data-testid="sop-intel-days"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="180">Last 180 days</SelectItem>
            <SelectItem value="365">Last 365 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <MiniStat label="Runs" value={d.totals.runs} testid="sop-intel-runs" />
        <MiniStat label="Completed" value={d.totals.completed} testid="sop-intel-completed" />
        <MiniStat label="Avg run time" value={fmtDuration(d.totals.avg_duration_seconds)} testid="sop-intel-avg" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Executor leaderboard */}
        <div className="border border-border" data-testid="sop-intel-leaderboard">
          <div className="px-3 py-2 label-eyebrow text-[10px] bg-muted/30 border-b border-border flex items-center gap-1.5">
            <Trophy className="w-3 h-3 text-amber-500" /> Executor performance (speed vs SOP average)
          </div>
          {(d.leaderboard || []).length === 0 ? <div className="p-4 text-xs text-muted-foreground">No completed runs yet.</div>
            : (d.leaderboard || []).map((u, i) => (
              <div key={u.user_id} className="flex items-center gap-2 px-3 py-2 border-b border-border last:border-b-0 text-sm" data-testid={`sop-intel-exec-${i}`}>
                <span className={`font-mono text-xs w-5 ${i === 0 ? "text-amber-500 font-bold" : "text-muted-foreground"}`}>#{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="truncate font-semibold text-xs">{u.user?.name || u.user?.email || u.user_id.slice(0, 8)}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {u.completed}/{u.runs} completed · avg {fmtDuration(u.avg_duration_seconds)} · skips {u.skip_rate_pct}%
                  </div>
                </div>
                <SpeedBadge score={u.speed_score} />
              </div>
            ))}
          <div className="px-3 py-1.5 text-[9px] text-muted-foreground bg-muted/20">
            Speed score 100 = SOP average pace. 150 = 1.5× faster than average. Based on completed, timed runs.
          </div>
        </div>

        {/* Slowest steps */}
        <div className="border border-border" data-testid="sop-intel-slowest">
          <div className="px-3 py-2 label-eyebrow text-[10px] bg-muted/30 border-b border-border flex items-center gap-1.5">
            <Turtle className="w-3 h-3 text-red-500" /> Slowest steps (measured)
          </div>
          {(d.slowest_steps || []).length === 0 ? <div className="p-4 text-xs text-muted-foreground">No timed step completions yet — durations start recording from now on.</div>
            : (d.slowest_steps || []).map((s, i) => (
              <div key={s.step_id} className="flex items-center gap-2 px-3 py-2 border-b border-border last:border-b-0 text-xs" data-testid={`sop-intel-slow-${i}`}>
                <div className="flex-1 min-w-0">
                  <div className="truncate font-semibold">{s.step_title}</div>
                  <div className="text-[10px] text-muted-foreground truncate">{s.sop_title} · {s.samples} samples</div>
                </div>
                <span className="font-mono text-red-600 font-bold">{fmtDuration(s.avg_seconds)}</span>
                <span className="font-mono text-[10px] text-muted-foreground">max {fmtDuration(s.max_seconds)}</span>
              </div>
            ))}
        </div>
      </div>

      {/* Weekly trend */}
      <div className="border border-border p-3" data-testid="sop-intel-trend">
        <div className="label-eyebrow text-[10px] mb-2 flex items-center gap-1.5"><TrendingUp className="w-3 h-3" /> Weekly run volume</div>
        {(d.weekly_trend || []).length === 0 ? <div className="text-xs text-muted-foreground py-4">No runs in this window.</div> : (
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={d.weekly_trend}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="week" tick={{ fontSize: 10 }} tickFormatter={(w) => w.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey="runs" name="Runs started" fill="hsl(220 70% 55%)" />
              <Line type="monotone" dataKey="completed" name="Completed" stroke="hsl(150 60% 40%)" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function SpeedBadge({ score }) {
  if (score == null) return <span className="chip text-[9px]">unrated</span>;
  const tone = score >= 120 ? "bg-emerald-500/15 text-emerald-600" : score >= 85 ? "bg-blue-500/15 text-blue-600" : "bg-amber-500/15 text-amber-600";
  return <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 ${tone}`} title="Speed score vs SOP average (100 = average)">⚡{score}</span>;
}

function MiniStat({ label, value, testid }) {
  return (
    <div className="border border-border p-3" data-testid={testid}>
      <div className="label-eyebrow text-[10px]">{label}</div>
      <div className="text-xl font-black font-mono">{value ?? "—"}</div>
    </div>
  );
}
